import { useEffect, useRef } from 'react'
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Typography,
} from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { tenantToday } from '@/shared/lib/dayRange'
import { formatDate, getCurrencyCode } from '@/shared/lib/format'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import { formatMoneyValue, money, type Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { withCurrentOption } from '@/shared/ui/currentOption'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { CashBranchSelect } from '@/features/cashdesk/CashBranchSelect'
import { SubscriberPicker } from '@/features/cashdesk/SubscriberPicker'
import { useCashBranches } from '@/features/cashdesk/useCashBranches'
import {
  fetchPaymentCategories,
  paymentCategoryName,
  type PaymentCategory,
} from '@/features/cashdesk/paymentCategoryApi'
import { fetchStockItems, fetchWarehouses, type StockItem } from '@/features/inventory/stockApi'
import {
  DEFAULT_DUE_DAYS,
  INVOICE_DESCRIPTION_MAX,
  INVOICE_LINE_KINDS,
  INVOICE_TEXT_MAX,
  createInvoice,
  defaultCategoryCode,
  fetchBillable,
  invoiceTotals,
  updateInvoice,
  type InvoiceDetail,
  type InvoiceDraft,
  type InvoiceLineKind,
} from './invoiceApi'

interface LineForm {
  kind: InvoiceLineKind
  description?: string
  quantity: number
  unitPrice: Money | null
  grantId?: string | null
  categoryId?: string | null
  itemId?: string | null
  warehouseId?: string | null
}

interface InvoiceForm {
  subscriberId?: string | null
  branchId?: string | null
  invoiceDate: Dayjs
  dueDate: Dayjs
  address?: string
  notes?: string
  discount?: Money | null
  discountReason?: string
  lines: LineForm[]
}

interface InvoiceFormModalProps {
  open: boolean
  invoice?: InvoiceDetail | null
  subscriberId?: string | null
  onClose: () => void
  onSaved: (invoice: InvoiceDetail) => void
}

const ISO_DAY = 'YYYY-MM-DD'
const FIXED_KINDS: readonly InvoiceLineKind[] = ['PACKAGE', 'PREVIOUS_BALANCE']

export function InvoiceFormModal({ open, invoice, subscriberId, onClose, onSaved }: InvoiceFormModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { can } = usePermissions()
  const queryClient = useQueryClient()
  const branches = useCashBranches()
  const [form] = Form.useForm<InvoiceForm>()
  const key = useRef('')
  const currency = user?.tenant.currency ?? getCurrencyCode()
  const editing = Boolean(invoice)
  const needsBranchChoice = !branches.unknown && branches.active.length > 1

  useEffect(() => {
    if (!open) return
    key.current = newIdempotencyKey('invoice')
    const today = tenantToday()
    form.setFieldsValue(
      invoice
        ? {
            subscriberId: invoice.customer.subscriberId,
            branchId: invoice.branch.id,
            invoiceDate: dayjs(invoice.invoiceDate),
            dueDate: dayjs(invoice.dueDate),
            address: invoice.customer.address ?? undefined,
            notes: invoice.notes ?? undefined,
            discount: invoice.discount.amount > 0 ? invoice.discount : null,
            discountReason: invoice.discountReason ?? undefined,
            lines: invoice.lines.map((line) => ({
              kind: line.kind,
              description: line.description ?? undefined,
              quantity: line.quantity,
              unitPrice: line.unitPrice,
              grantId: line.grantId,
              categoryId: line.categoryId,
              itemId: line.itemId,
              warehouseId: line.warehouseId,
            })),
          }
        : {
            subscriberId: subscriberId ?? null,
            branchId: branches.sole?.id ?? null,
            invoiceDate: today,
            dueDate: today.add(DEFAULT_DUE_DAYS, 'day'),
            discount: null,
            lines: [{ kind: 'INSTALLATION', quantity: 1, unitPrice: null }],
          },
    )
  }, [open, invoice, subscriberId, branches.sole?.id, form])

  const watchedSubscriber = Form.useWatch('subscriberId', form) ?? null
  const lines = Form.useWatch('lines', form) ?? []
  const discount = Form.useWatch('discount', form)?.amount ?? 0

  const billable = useQuery({
    queryKey: queryKeys.invoices.billable(watchedSubscriber ?? ''),
    queryFn: ({ signal }) => fetchBillable(watchedSubscriber ?? '', signal),
    enabled: open && watchedSubscriber !== null,
  })

  const canReadStock = can('inventory.stock.read')
  const hasEquipment = lines.some((line) => line?.kind === 'EQUIPMENT')
  const categories = useQuery({
    queryKey: queryKeys.paymentCategories,
    queryFn: ({ signal }) => fetchPaymentCategories(signal),
    enabled: open,
    staleTime: 5 * 60_000,
  })
  const stockItems = useQuery({
    queryKey: queryKeys.stock.items(false),
    queryFn: () => fetchStockItems(false),
    enabled: open && canReadStock && hasEquipment,
  })
  const warehouses = useQuery({
    queryKey: queryKeys.stock.warehouses,
    queryFn: fetchWarehouses,
    enabled: open && canReadStock && hasEquipment,
  })

  const categoryOption = (c: PaymentCategory) => ({ value: c.id, label: paymentCategoryName(c, t) })
  const itemOption = (item: StockItem) => ({ value: item.id, label: `${item.name} · ${item.sku}` })
  const knownCategory = (id: string) => {
    const category = categories.data?.find((c) => c.id === id)
    return category ? categoryOption(category) : null
  }
  const knownItem = (id: string) => {
    const item = stockItems.data?.find((candidate) => candidate.id === id)
    return item ? itemOption(item) : null
  }

  const categoryPlaceholder = (kind: InvoiceLineKind) => {
    const fallback = (categories.data ?? []).find((c) => c.code === defaultCategoryCode(kind))
    return fallback ? t('invoices.form.categoryDefault', { name: paymentCategoryName(fallback, t) }) : undefined
  }

  const sales = [
    ...(billable.data?.sales ?? []),
    ...(invoice?.lines ?? [])
      .filter((line) => line.kind === 'PACKAGE' && line.grantId)
      .filter((line) => !(billable.data?.sales ?? []).some((s) => s.grantId === line.grantId))
      .map((line) => ({
        grantId: line.grantId ?? '',
        planId: null,
        planName: line.description,
        charged: line.unitPrice,
        soldAt: '',
        state: null,
      })),
  ]

  const save = useMutation({
    mutationFn: (values: InvoiceForm) => {
      const draft: InvoiceDraft = {
        subscriberId: values.subscriberId ?? '',
        branchId: branches.sole?.id ?? values.branchId ?? null,
        invoiceDate: values.invoiceDate.format(ISO_DAY),
        dueDate: values.dueDate.format(ISO_DAY),
        address: values.address?.trim() || null,
        notes: values.notes?.trim() || null,
        discount: values.discount?.amount ?? null,
        discountReason: values.discountReason?.trim() || null,
        lines: values.lines.map((line) => ({
          kind: line.kind,
          description: line.description?.trim() || null,
          quantity: line.quantity,
          unitPrice: line.unitPrice?.amount ?? 0,
          grantId: line.kind === 'PACKAGE' ? (line.grantId ?? null) : null,
          categoryId: line.categoryId ?? null,
          itemId: line.kind === 'EQUIPMENT' ? (line.itemId ?? null) : null,
          warehouseId: line.kind === 'EQUIPMENT' ? (line.warehouseId ?? null) : null,
        })),
      }
      return invoice ? updateInvoice(invoice.id, draft) : createInvoice(draft, key.current)
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all })
      onSaved(saved)
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  const setLine = (index: number, patch: Partial<LineForm>) => {
    const current = form.getFieldValue('lines') as LineForm[]
    form.setFieldValue(
      'lines',
      current.map((line, i) => (i === index ? { ...line, ...patch } : line)),
    )
  }

  const onKindChange = (index: number, kind: InvoiceLineKind) => {
    const reset = { categoryId: null, itemId: null, warehouseId: null }
    if (kind === 'PREVIOUS_BALANCE') {
      setLine(index, {
        ...reset,
        kind,
        quantity: 1,
        grantId: null,
        description: t('invoices.lineKind.PREVIOUS_BALANCE'),
        unitPrice: billable.data?.outstanding ?? null,
      })
    } else if (kind === 'PACKAGE') {
      setLine(index, { ...reset, kind, quantity: 1, grantId: null, unitPrice: null, description: undefined })
    } else {
      setLine(index, { ...reset, kind, grantId: null })
    }
  }

  const onItemChange = (index: number, itemId: string | undefined) => {
    const item = (stockItems.data ?? []).find((candidate) => candidate.id === itemId)
    const current = (form.getFieldValue('lines') as LineForm[])[index]
    setLine(index, {
      itemId: itemId ?? null,
      ...(item && !current?.description?.trim() ? { description: item.name } : {}),
    })
  }

  const onGrantChange = (index: number, grantId: string | undefined) => {
    const sale = sales.find((s) => s.grantId === grantId)
    setLine(index, {
      grantId: grantId ?? null,
      quantity: 1,
      unitPrice: sale?.charged ?? null,
      description: sale?.planName ?? undefined,
    })
  }

  const usedGrants = new Set(lines.map((line) => line?.grantId).filter(Boolean))
  const totals = invoiceTotals(
    lines.map((line) => ({ quantity: line?.quantity ?? 0, unitPrice: line?.unitPrice?.amount ?? 0 })),
    discount,
  )

  return (
    <Modal
      open={open}
      width={920}
      title={editing ? t('invoices.form.editTitle') : t('invoices.form.createTitle')}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={save.isPending}
      onCancel={close}
      destroyOnHidden
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('invoices.form.saleOnceNote')} />
        {hasEquipment ? <Alert type="info" showIcon message={t('invoices.form.equipmentNote')} /> : null}
        {hasEquipment && !canReadStock ? (
          <Alert type="warning" showIcon message={t('invoices.form.stockAccessRequired')} />
        ) : null}
        {save.isError ? <Alert type="error" showIcon message={translateError(save.error, t)} /> : null}
        {billable.isError ? <Alert type="error" showIcon message={translateError(billable.error, t)} /> : null}
        {categories.isError ? <Alert type="error" showIcon message={translateError(categories.error, t)} /> : null}
        {stockItems.isError || warehouses.isError ? (
          <Alert type="error" showIcon message={translateError(stockItems.error ?? warehouses.error, t)} />
        ) : null}

        <Form<InvoiceForm> form={form} layout="vertical" requiredMark preserve={false}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="subscriberId"
                label={t('invoices.form.subscriber')}
                rules={[{ required: true, message: t('invoices.form.subscriberRequired') }]}
              >
                <SubscriberPicker disabled={editing || Boolean(subscriberId)} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            {needsBranchChoice ? (
              <Col xs={24} md={12}>
                <Form.Item
                  name="branchId"
                  label={t('cashdesk.branch')}
                  rules={[{ required: true, message: t('cashdesk.branchRequired') }]}
                >
                  <CashBranchSelect branches={branches.active} loading={branches.isPending} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            ) : null}
            <Col xs={12} md={6}>
              <Form.Item
                name="invoiceDate"
                label={t('invoices.form.invoiceDate')}
                rules={[{ required: true, message: t('invoices.form.dateRequired') }]}
              >
                <DatePicker allowClear={false} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="dueDate"
                label={t('invoices.form.dueDate')}
                dependencies={['invoiceDate']}
                rules={[
                  { required: true, message: t('invoices.form.dateRequired') },
                  ({ getFieldValue }) => ({
                    validator: (_, value: Dayjs | undefined) => {
                      const start = getFieldValue('invoiceDate') as Dayjs | undefined
                      return !value || !start || !value.isBefore(start, 'day')
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('invoices.form.dueBeforeDate')))
                    },
                  }),
                ]}
              >
                <DatePicker allowClear={false} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="address" label={t('invoices.form.address')}>
                <Input maxLength={INVOICE_DESCRIPTION_MAX} />
              </Form.Item>
            </Col>
          </Row>

          <Typography.Title level={5}>{t('invoices.form.lines')}</Typography.Title>
          <Form.List
            name="lines"
            rules={[
              {
                validator: (_, value: LineForm[] | undefined) =>
                  value && value.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('invoices.form.linesRequired'))),
              },
            ]}
          >
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map((field, index) => {
                  const kind = lines[index]?.kind ?? 'OTHER'
                  const fixed = FIXED_KINDS.includes(kind)
                  return (
                    <Row key={field.key} gutter={8} align="top">
                      <Col xs={24} md={5}>
                        <Form.Item name={[field.name, 'kind']} label={index === 0 ? t('invoices.form.kind') : undefined}>
                          <Select<InvoiceLineKind>
                            onChange={(value) => onKindChange(index, value)}
                            options={INVOICE_LINE_KINDS.map((value) => ({
                              value,
                              label: t(`invoices.lineKind.${value}`),
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        {kind === 'PACKAGE' ? (
                          <Form.Item
                            name={[field.name, 'grantId']}
                            label={index === 0 ? t('invoices.form.description') : undefined}
                            rules={[{ required: true, message: t('invoices.form.saleRequired') }]}
                          >
                            <Select<string>
                              placeholder={t('invoices.form.salePlaceholder')}
                              loading={billable.isFetching}
                              onChange={(value) => onGrantChange(index, value)}
                              notFoundContent={t('invoices.form.noSales')}
                              options={sales
                                .filter((s) => s.grantId === lines[index]?.grantId || !usedGrants.has(s.grantId))
                                .map((s) => ({
                                  value: s.grantId,
                                  label: [s.planName ?? t('common.dash'), formatMoneyValue(s.charged), s.soldAt ? formatDate(s.soldAt) : null]
                                    .filter(Boolean)
                                    .join(' · '),
                                }))}
                            />
                          </Form.Item>
                        ) : (
                          <Form.Item
                            name={[field.name, 'description']}
                            label={index === 0 ? t('invoices.form.description') : undefined}
                            rules={
                              fixed
                                ? []
                                : [{ required: true, whitespace: true, message: t('invoices.form.descriptionRequired') }]
                            }
                          >
                            <Input maxLength={INVOICE_DESCRIPTION_MAX} disabled={fixed} />
                          </Form.Item>
                        )}
                      </Col>
                      <Col xs={8} md={3}>
                        <Form.Item
                          name={[field.name, 'quantity']}
                          label={index === 0 ? t('invoices.form.quantity') : undefined}
                          rules={[{ required: true, message: t('invoices.form.quantityRequired') }]}
                        >
                          <InputNumber min={1} precision={0} disabled={fixed} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Item
                          name={[field.name, 'unitPrice']}
                          label={index === 0 ? t('invoices.form.unitPrice') : undefined}
                          rules={
                            fixed
                              ? []
                              : [
                                  {
                                    validator: (_, value: Money | null | undefined) =>
                                      value && value.amount > 0
                                        ? Promise.resolve()
                                        : Promise.reject(new Error(t('invoices.form.priceRequired'))),
                                  },
                                ]
                          }
                        >
                          <MoneyInput currency={currency} minMinor={0} disabled={fixed} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={4} md={2}>
                        <Form.Item label={index === 0 ? ' ' : undefined}>
                          <Button
                            icon={<DeleteOutlined />}
                            aria-label={t('invoices.form.removeLine')}
                            disabled={fields.length === 1}
                            onClick={() => remove(field.name)}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={kind === 'EQUIPMENT' ? 7 : 12}>
                        <Form.Item name={[field.name, 'categoryId']}>
                          <Select<string>
                            allowClear
                            prefix={t('invoices.form.category')}
                            placeholder={categoryPlaceholder(kind)}
                            loading={categories.isPending}
                            options={withCurrentOption(
                              (categories.data ?? []).filter((c) => c.active).map(categoryOption),
                              lines[index]?.categoryId,
                              knownCategory,
                            )}
                          />
                        </Form.Item>
                      </Col>
                      {kind === 'EQUIPMENT' ? (
                        <>
                          <Col xs={24} md={9}>
                            <Form.Item
                              name={[field.name, 'itemId']}
                              rules={[{ required: true, message: t('invoices.form.itemRequired') }]}
                            >
                              <Select<string>
                                showSearch
                                optionFilterProp="label"
                                prefix={t('invoices.form.item')}
                                loading={stockItems.isFetching}
                                onChange={(value) => onItemChange(index, value)}
                                options={withCurrentOption(
                                  (stockItems.data ?? []).filter((item) => item.active).map(itemOption),
                                  lines[index]?.itemId,
                                  knownItem,
                                )}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={6}>
                            <Form.Item
                              name={[field.name, 'warehouseId']}
                              rules={[{ required: true, message: t('invoices.form.warehouseRequired') }]}
                            >
                              <Select<string>
                                showSearch
                                optionFilterProp="label"
                                prefix={t('invoices.form.warehouse')}
                                loading={warehouses.isFetching}
                                options={(warehouses.data ?? [])
                                  .filter((w) => w.state !== 'CLOSED' || w.id === lines[index]?.warehouseId)
                                  .map((w) => ({ value: w.id, label: w.displayName }))}
                              />
                            </Form.Item>
                          </Col>
                        </>
                      ) : null}
                    </Row>
                  )
                })}
                <Form.ErrorList errors={errors} />
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => add({ kind: 'SERVICE', quantity: 1, unitPrice: null })}
                  style={{ marginBottom: 16 }}
                >
                  {t('invoices.form.addLine')}
                </Button>
              </>
            )}
          </Form.List>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="discount"
                label={t('invoices.form.discount')}
                rules={[
                  {
                    validator: (_, value: Money | null | undefined) =>
                      !value || value.amount <= totals.subtotal
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('sales.discountTooLarge'))),
                  },
                ]}
              >
                <MoneyInput currency={currency} minMinor={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            {discount > 0 ? (
              <Col xs={24} md={16}>
                <Form.Item
                  name="discountReason"
                  label={t('sales.discountReason')}
                  rules={[
                    { required: true, whitespace: true, message: t('sales.discountReasonRequired') },
                    { max: INVOICE_TEXT_MAX, message: t('cashdesk.reasonTooLong') },
                  ]}
                >
                  <Input maxLength={INVOICE_TEXT_MAX} />
                </Form.Item>
              </Col>
            ) : null}
          </Row>

          <Form.Item name="notes" label={t('invoices.form.notes')}>
            <Input.TextArea rows={2} maxLength={INVOICE_TEXT_MAX} showCount />
          </Form.Item>
        </Form>

        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label={t('invoices.subtotal')}>
            {formatMoneyValue(money(totals.subtotal, currency))}
          </Descriptions.Item>
          <Descriptions.Item label={t('invoices.discount')}>{formatMoneyValue(money(discount, currency))}</Descriptions.Item>
          <Descriptions.Item label={t('invoices.total')}>{formatMoneyValue(money(totals.total, currency))}</Descriptions.Item>
        </Descriptions>
      </Space>
    </Modal>
  )
}
